// Accent-insensitive matching: French recipes are searched as "creme" as often as "crème".
const fold = (text) => text.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

class FkRecipeIndex extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.grid = this.querySelector('[data-recipe-grid]');
    this.pagination = this.querySelector('[data-pagination]');
    this.next = new URL(location.pathname, location.origin).href;
    this.heading = this.querySelector('[data-library-title]');
    this.originalTitle = this.heading?.textContent;
    this.originalStatus = this.querySelector('[data-results]').textContent;
    this.abort = new AbortController();
    const options = {signal: this.abort.signal};
    this.querySelector('form').addEventListener('submit', async event => {
      event.preventDefault();
      await this.search();
      this.querySelector('[data-recipe-library]')?.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start'});
    }, options);
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(), 200);
    }, options);
    this.querySelector('[data-clear]').addEventListener('click', () => { this.input.value = ''; this.search(); this.input.focus(); }, options);
    this.querySelector('[data-retry]').addEventListener('click', () => this.search(), options);
  }
  async loadAll() {
    if (this.loading) return this.loading;
    this.loading = (async () => {
      while (this.next) {
        const url = new URL(this.next);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, {signal: this.abort.signal});
        if (!response.ok) throw new Error('Recipe request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = document.querySelector('fk-recipe-index');
        if (!section) throw new Error('Recipe list unavailable');
        const existing = new Set([...this.grid.querySelectorAll('[data-recipe-card] a')].map(link => link.getAttribute('href')));
        for (const card of section.querySelectorAll('[data-recipe-card]')) {
          if (existing.has(card.querySelector('a').getAttribute('href'))) continue;
          card.dataset.loaded = 'true';
          card.hidden = true;
          this.grid.append(card);
        }
        const next = section.querySelector('[data-next-page]')?.getAttribute('href');
        this.next = next ? new URL(next, this.dataset.blogUrl ? location.origin + this.dataset.blogUrl : location.href).href : null;
      }
    })();
    try { await this.loading; } finally { this.loading = null; }
  }
  async search() {
    const query = fold(this.input.value.trim());
    const request = this.request = (this.request || 0) + 1;
    this.querySelector('[data-clear]').hidden = !query;
    if (this.heading) this.heading.textContent = query ? this.dataset.searchTitle.replace('[query]', this.input.value.trim()) : this.originalTitle;
    this.querySelector('[data-search-error]').hidden = true;
    if (query) {
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-results]').textContent = this.dataset.loadingLabel || '';
      try { await this.loadAll(); }
      catch (error) {
        if (error.name !== 'AbortError' && request === this.request) this.querySelector('[data-search-error]').hidden = false;
      }
      finally { if (request === this.request) this.removeAttribute('aria-busy'); }
    } else this.removeAttribute('aria-busy');
    if (request !== this.request) return;
    let count = 0;
    for (const card of this.grid.querySelectorAll('[data-recipe-card]')) {
      const text = fold(`${card.dataset.search} ${card.dataset.tags}`);
      const match = query ? query.split(/\s+/).every(word => text.includes(word)) : !card.dataset.loaded;
      card.hidden = !match;
      if (match) count++;
    }
    this.pagination.hidden = !!query;
    this.querySelector('[data-empty]').hidden = count > 0;
    this.querySelector('[data-results]').textContent = query ? this.resultsLabel(count) : this.originalStatus;
    // Analytics (assets/fk-analytics.js) reports the term once typing settles (as typed, lower case, accents kept).
    if (query) document.dispatchEvent(new CustomEvent('fk:track', { detail: { event: 'recipe_search_performed', settle: 'search:recipes', properties: { search_term: this.input.value.trim().toLocaleLowerCase(), results_count: count } } }));
  }
  resultsLabel(count) {
    let form = count === 1 ? 'one' : 'other';
    try { form = new Intl.PluralRules(document.documentElement.lang || undefined).select(count); } catch { /* keep fallback */ }
    const label = form === 'one' && this.dataset.resultsOne ? this.dataset.resultsOne : this.dataset.resultsLabel;
    return label.replace('[count]', count);
  }
  disconnectedCallback() { clearTimeout(this.timer); this.abort.abort(); }
}
if (!customElements.get('fk-recipe-index')) customElements.define('fk-recipe-index', FkRecipeIndex);
